import React, { useEffect, useState } from "react";
import { toastr } from 'react-redux-toastr';
import { confirmAlert } from "react-confirm-alert";
import { Card, CardHeader, CardBody, Row, Col, Button, Input, Label, FormGroup, Table, Alert } from "reactstrap";
import general_fetch from "../../utils/fetch";
import DeleteNotification from "../../components/DeleteNotification";

// Restarts a churned org on the same account, on a new plan (backend: POST csp/restart_churned_org, CS admins only).
// The org is unchurned and re-activated, its cycle re-anchored at the restart date and its due re-minted from the
// new subscription. Preview is a dry run; the restart runs only the exact request that was previewed.

const DUE_TREATMENTS = [
	{ key: 'write_off', label: 'Write off old arrears', hint: 'Credit back what they owed for past months. The new due is just the next cycle on the new plan.' },
	{ key: 'keep_arrears', label: 'Keep old arrears', hint: 'They still owe past months. The new due is the next cycle on the new plan plus those arrears.' },
	{ key: 'keep_full', label: 'Keep old due as is', hint: 'Keep the old due amount and only move its dates. The new plan applies from the next cycle.' },
];

// The admin's local calendar date; toISOString() would give yesterday before 05:30 IST.
const today = () => {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const format_day = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '-';
const format_inr = (amount) => amount == null ? '-' : `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const yes_no = (v) => v ? 'Yes' : 'No';

const RestartChurnedOrg = () => {
	const [plans, set_plans] = useState([]);
	const [add_ons, set_add_ons] = useState([]);
	const [form, set_form] = useState({ org_id: '', restart_date: today(), plan_id: '9', additional_users: 0, add_on_ids: [], previous_due_treatment: 'write_off', write_off_note: '', due_date: '' });
	const [preview, set_preview] = useState(null);
	const [running, set_running] = useState(false);

	useEffect(() => {
		// Restart is India-only on the backend, so India pricing is the one that applies.
		general_fetch({ url: 'prepaid_signup_flow/get_all_plans_with_pricing', body: { country: 'IN' } })
			.then(resp => {
				set_plans((resp.plans || []).filter(p => !p.name.toLowerCase().includes('old')));
				set_add_ons(resp.add_ons || []);
			})
			.catch(err => { console.error(err); toastr.error('Could not load plans'); });
	}, []);

	// Any change makes the preview stale, so the restart can only run what was previewed.
	const update = (changes) => { set_form({ ...form, ...changes }); set_preview(null); };
	const toggle_add_on = (id) => update({ add_on_ids: form.add_on_ids.includes(id) ? form.add_on_ids.filter(x => x !== id) : [...form.add_on_ids, id] });
	const plan_name = (id) => { const plan = plans.find(p => p.id === String(id)); return plan ? plan.name : `Plan ${id}`; };
	const selected_plan = plans.find(p => p.id === form.plan_id);

	const request_body = () => ({
		...form,
		org_id: form.org_id.trim(),
		additional_users: Number(form.additional_users) || 0,
		due_date: form.due_date || null,
	});

	const run = async (dry_run) => {
		set_running(true);
		try {
			const body = dry_run ? request_body() : { ...preview.request, expected_due_order_id: preview.before.due ? preview.before.due.order_id : null };
			const resp = await general_fetch({ url: 'csp/restart_churned_org', body: { ...body, dry_run }, req_resp: true });
			if (resp && resp.response_code === 1) {
				if (dry_run) {
					set_preview({ ...resp.data, request: body });
				} else {
					set_preview(null);
					toastr.success(`${resp.data.org.name} restarted`, resp.data.after.due ? `New due ${format_inr(resp.data.after.due.total)}, due ${format_day(resp.data.after.due.due_date)}` : 'Nothing is due');
				}
			} else {
				toastr.error(dry_run ? 'Preview failed' : 'Restart failed', (resp && resp.message) || 'Unknown error');
			}
		} catch (err) {
			console.error(err);
			toastr.error(dry_run ? 'Preview failed' : 'Restart failed', err.message || 'Network error');
		} finally {
			set_running(false);
		}
	};

	const confirm_restart = () => confirmAlert({
		customUI: ({ onClose }) => (
			<DeleteNotification yes_del={() => run(false)} no_del={() => {}} onClose={onClose} button_name='Restart org'
				text={`Restart ${preview.org.name} on ${preview.plan.name} from ${format_day(preview.restart_date)}? Its due changes from ${preview.before.due ? format_inr(preview.before.due.total) : 'nothing'} to ${preview.after.due ? format_inr(preview.after.due.total) : 'nothing'}.`} />
		),
	});

	const rows = preview ? [
		['Plan', plan_name(preview.before.plan_id), preview.plan.name],
		['Churned', yes_no(preview.before.churned), yes_no(preview.after.churned)],
		['Suspended', yes_no(preview.before.suspended), yes_no(preview.after.suspended)],
		['Billing anchor', format_day(preview.before.billing_anchor_date), format_day(preview.after.billing_anchor_date)],
		['Account balance (credits)', preview.before.balance, preview.after.balance],
		['Due total', preview.before.due ? format_inr(preview.before.due.total) : 'None', preview.after.due ? format_inr(preview.after.due.total) : 'None'],
		['Due date', preview.before.due ? format_day(preview.before.due.due_date) : '-', preview.after.due ? format_day(preview.after.due.due_date) : '-'],
	] : [];

	return (
		<Card className="shadow mb-4">
			<CardHeader className="border-0">
				<h3 className="mb-1">Restart churned org</h3>
				<div style={{ fontSize: '13px', color: '#666' }}>
					Bring a churned org back on the same account and a new plan. It is unchurned and re-activated, its billing cycle
					restarts on the restart date, and its due is rebuilt from the new plan. The months it was churned are not billed.
					Preview first: nothing is saved until you restart. India orgs only; usage-based orgs are not supported.
				</div>
			</CardHeader>
			<CardBody className="pt-0">
				<Row>
					<Col md="4">
						<FormGroup>
							<Label for="restart_org_id" style={{ fontSize: '13px' }}>Org ID</Label>
							<Input id="restart_org_id" bsSize="sm" value={form.org_id} placeholder="e.g. a47cbb507abb6558" onChange={e => update({ org_id: e.target.value })} />
						</FormGroup>
					</Col>
					<Col md="4">
						<FormGroup>
							<Label for="restart_date" style={{ fontSize: '13px' }}>Restart date (the date they paid)</Label>
							<Input id="restart_date" type="date" bsSize="sm" value={form.restart_date} onChange={e => update({ restart_date: e.target.value })} />
						</FormGroup>
					</Col>
					<Col md="4">
						<FormGroup>
							<Label for="restart_due_date" style={{ fontSize: '13px' }}>Due date (optional)</Label>
							<Input id="restart_due_date" type="date" bsSize="sm" value={form.due_date} onChange={e => update({ due_date: e.target.value })} />
							<div style={{ fontSize: '11px', color: '#888' }}>Default: 7 days after the restart date</div>
						</FormGroup>
					</Col>
				</Row>
				<Row>
					<Col md="4">
						<FormGroup>
							<Label for="restart_plan" style={{ fontSize: '13px' }}>New plan</Label>
							<Input id="restart_plan" type="select" bsSize="sm" value={form.plan_id} onChange={e => update({ plan_id: e.target.value })}>
								{plans.map(p => (
									<option key={p.id} value={p.id}>
										{p.name} ({p.free_users} users, {format_inr(p.pricing && p.pricing.base_price)} / {p.payment_cycle} mo)
									</option>
								))}
							</Input>
						</FormGroup>
					</Col>
					<Col md="4">
						<FormGroup>
							<Label for="restart_additional_users" style={{ fontSize: '13px' }}>Additional users</Label>
							<Input id="restart_additional_users" type="number" min="0" step="1" bsSize="sm" value={form.additional_users} onChange={e => update({ additional_users: e.target.value })} />
							{selected_plan ? <div style={{ fontSize: '11px', color: '#888' }}>On top of the plan's {selected_plan.free_users} users, {format_inr(selected_plan.pricing && selected_plan.pricing.additional_user_price)} each per cycle</div> : null}
						</FormGroup>
					</Col>
					<Col md="4">
						<Label style={{ fontSize: '13px' }}>Add-ons</Label>
						<div style={{ fontSize: '12px' }}>
							{add_ons.map(a => (
								<FormGroup check key={a.id} className="mb-0">
									<Label check>
										<Input type="checkbox" checked={form.add_on_ids.includes(a.id)} onChange={() => toggle_add_on(a.id)} /> {a.name}
									</Label>
								</FormGroup>
							))}
						</div>
						<div style={{ fontSize: '11px', color: '#888' }}>Business and Enterprise plans add Advanced Pricing and MES Pro automatically</div>
					</Col>
				</Row>
				<Row className="mt-2">
					<Col md="8">
						<Label style={{ fontSize: '13px' }}>Old unpaid due</Label>
						{DUE_TREATMENTS.map(t => (
							<FormGroup check key={t.key} className="mb-1">
								<Label check style={{ fontSize: '13px' }}>
									<Input type="radio" name="restart_due_treatment" checked={form.previous_due_treatment === t.key} onChange={() => update({ previous_due_treatment: t.key })} /> {t.label}
									<div style={{ fontSize: '11px', color: '#888' }}>{t.hint}</div>
								</Label>
							</FormGroup>
						))}
					</Col>
					{form.previous_due_treatment === 'write_off' ? (
						<Col md="4">
							<FormGroup>
								<Label for="restart_write_off_note" style={{ fontSize: '13px' }}>Write-off note</Label>
								<Input id="restart_write_off_note" bsSize="sm" maxLength={256} value={form.write_off_note} placeholder="e.g. Arrears waived on return" onChange={e => update({ write_off_note: e.target.value })} />
							</FormGroup>
						</Col>
					) : null}
				</Row>
				<Button color="primary" size="sm" className="mt-2" disabled={running || !form.org_id.trim() || !form.restart_date || !form.plan_id} onClick={() => run(true)}>
					{running && !preview ? <i className="fa fa-spinner fa-spin mr-1" /> : <i className="fa fa-eye mr-1" />}Preview
				</Button>

				{preview ? (
					<div className="mt-4">
						<h4 className="mb-2">Preview: {preview.org.name} <span style={{ fontSize: '12px', color: '#888' }}>({preview.org.id})</span></h4>
						<div style={{ fontSize: '13px' }} className="mb-2">
							Restart on <b>{format_day(preview.restart_date)}</b> on <b>{preview.plan.name}</b>, {preview.payment_cycle}-month cycle,
							{' '}{preview.additional_users} additional users{preview.written_off_credits > 0 ? <>, writing off <b>{preview.written_off_credits}</b> credits of arrears</> : null}.
						</div>
						<Table size="sm" bordered style={{ fontSize: '13px', maxWidth: '640px' }}>
							<thead><tr><th></th><th>Now</th><th>After restart</th></tr></thead>
							<tbody>
								{rows.map(([label, before, after]) => (
									<tr key={label}><td>{label}</td><td>{before}</td><td style={{ fontWeight: String(before) === String(after) ? 400 : 700 }}>{after}</td></tr>
								))}
							</tbody>
						</Table>
						{preview.warnings.map(w => <Alert color="warning" key={w} className="py-2" style={{ fontSize: '13px' }}>{w}</Alert>)}
						<Button color="danger" size="sm" disabled={running} onClick={confirm_restart}>
							{running ? <i className="fa fa-spinner fa-spin mr-1" /> : null}Restart {preview.org.name}
						</Button>
					</div>
				) : null}
			</CardBody>
		</Card>
	);
};

export default RestartChurnedOrg;
